import React, { useState, useEffect, useContext, useCallback } from 'react';
import { rolesAPI } from '../../api/roles';
import { NotificationContext } from '../../context/NotificationContext';
import Button from '../common/Button';
import Modal from '../common/Modal';
import './RoleManagement.css';

// ─── Constants ────────────────────────────────────────────────────────────────

const TABS = [
  { id: 'roles', label: 'Roles & Permissions', icon: '🔐' },
  { id: 'users', label: 'Employee Access',      icon: '👥' },
];

const MODULES = [
  { key: 'dashboard',     label: 'Dashboard',     icon: '📊', perms: ['view', null,      null,       null    ] },
  { key: 'pos',           label: 'POS',            icon: '💳', perms: ['view', 'create',  null,       null    ] },
  { key: 'orders',        label: 'Orders',         icon: '📦', perms: ['view', 'create',  'edit',     'delete'] },
  { key: 'customers',     label: 'Customers',      icon: '👥', perms: ['view', 'create',  'edit',     'delete'] },
  { key: 'subscriptions', label: 'Subscriptions',  icon: '🏷️', perms: ['view', 'create',  'edit',     'delete'] },
  { key: 'services',      label: 'Services',       icon: '🧺', perms: ['view', 'create',  'edit',     'delete'] },
  { key: 'payments',      label: 'Payments',       icon: '💰', perms: ['view', 'create',  'refund',   null    ] },
  { key: 'wallet',        label: 'Wallet',         icon: '👛', perms: ['view', 'deposit', 'adjust',   'refund'] },
  { key: 'credit',        label: 'Credit',         icon: '🏦', perms: ['view', 'settle',  'setLimit', null    ] },
  { key: 'roles',         label: 'Roles',          icon: '🔐', perms: ['view', 'create',  'edit',     'delete'] },
];

const COL_HEADERS = ['View', 'Create / Deposit / Settle', 'Edit / Adjust / Set Limit', 'Delete / Refund'];

const EMPTY_PERMISSIONS = () =>
  Object.fromEntries(
    MODULES.map(m => [
      m.key,
      Object.fromEntries(m.perms.filter(Boolean).map(p => [p, false]))
    ])
  );

const buildEmptyRole = () => ({
  name: '',
  description: '',
  permissions: EMPTY_PERMISSIONS(),
});

const buildEmptyEmployee = () => ({
  name:     '',
  email:    '',
  phone:    '',
  password: '',
  role:     'staff',
  isActive: true,
});

const USER_ROLE_OPTIONS = ['admin', 'manager', 'cashier', 'staff'];

// ─── Sub-components ───────────────────────────────────────────────────────────

const RoleBadge = ({ name }) => {
  const colors = {
    admin:   { bg: '#fee2e2', color: '#991b1b' },
    manager: { bg: '#ede9fe', color: '#4c1d95' },
    cashier: { bg: '#dbeafe', color: '#1e40af' },
    staff:   { bg: '#f1f5f9', color: '#475569' },
  };
  const m = colors[name] || colors.staff;
  return (
    <span className="rm-badge" style={{ background: m.bg, color: m.color }}>
      {name ? name.charAt(0).toUpperCase() + name.slice(1) : '—'}
    </span>
  );
};

const StatusBadge = ({ active }) => (
  <span className="rm-badge" style={{
    background: active ? '#d1fae5' : '#fee2e2',
    color:      active ? '#065f46' : '#991b1b',
  }}>
    {active ? 'Active' : 'Inactive'}
  </span>
);

const SummaryCard = ({ icon, label, value, sub, accent }) => (
  <div className="ps-summary-card" style={{ borderTopColor: accent }}>
    <div className="ps-summary-icon">{icon}</div>
    <div>
      <div className="ps-summary-value" style={{ color: accent }}>{value}</div>
      <div className="ps-summary-label">{label}</div>
      {sub && <div className="ps-summary-sub">{sub}</div>}
    </div>
  </div>
);

// ─── Permission Toggle ────────────────────────────────────────────────────────

const PermToggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    className={`rm-toggle${checked ? ' on' : ' off'}${disabled ? ' readonly' : ''}`}
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    title={disabled ? 'System role — read only' : checked ? 'Revoke permission' : 'Grant permission'}
  >
    {checked ? '✓' : ''}
  </button>
);

// ─── Permission Grid ──────────────────────────────────────────────────────────

const PermissionGrid = ({ permissions, onChange, readOnly }) => {
  const handleToggle = (moduleKey, perm, val) => {
    const next = {
      ...permissions,
      [moduleKey]: { ...permissions[moduleKey], [perm]: val },
    };
    if (perm === 'view' && !val) {
      const mod = MODULES.find(x => x.key === moduleKey);
      mod.perms.filter(Boolean).forEach(p => { next[moduleKey][p] = false; });
    }
    if (perm !== 'view' && val) {
      next[moduleKey].view = true;
    }
    onChange(next);
  };

  return (
    <div>
      <div className="rm-perm-header">
        <div>Module</div>
        {COL_HEADERS.map(h => (
          <div key={h} style={{ textAlign: 'center' }}>{h}</div>
        ))}
      </div>
      {MODULES.map(module => {
        const modPerms = permissions?.[module.key] || {};
        return (
          <div key={module.key} className="rm-perm-row">
            <div className="rm-perm-module">
              <span className="rm-perm-module-icon">{module.icon}</span>
              {module.label}
            </div>
            {module.perms.map((perm, idx) => (
              <div key={idx} className="rm-perm-cell">
                {perm ? (
                  <PermToggle
                    checked={!!modPerms[perm]}
                    onChange={val => handleToggle(module.key, perm, val)}
                    disabled={readOnly}
                  />
                ) : (
                  <div className="rm-perm-dash">
                    <div className="rm-perm-dash-line" />
                  </div>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ─── Role Card ────────────────────────────────────────────────────────────────

const RoleCard = ({ role, isSelected, onClick, onEdit, onDelete }) => {
  const totalGranted = Object.values(role.permissions || {}).reduce(
    (sum, mod) => sum + Object.values(mod).filter(Boolean).length, 0
  );
  const totalPossible = MODULES.reduce((s, m) => s + m.perms.filter(Boolean).length, 0);
  const pct = totalPossible > 0 ? Math.round((totalGranted / totalPossible) * 100) : 0;

  return (
    <div
      className={`rm-role-card${isSelected ? ' selected' : ''}`}
      onClick={onClick}
    >
      {role.isSystem && <div className="rm-system-pill">System</div>}
      <div className="rm-role-card-header">
        <div className="rm-role-avatar">🔐</div>
        <div>
          <div className="rm-role-name">{role.name}</div>
          <div className="rm-role-desc">{role.description || 'No description'}</div>
        </div>
      </div>
      <div className="rm-coverage-label">
        <span className="rm-coverage-text">Access coverage</span>
        <span className="rm-coverage-count">{totalGranted}/{totalPossible}</span>
      </div>
      <div className="rm-coverage-bar">
        <div className="rm-coverage-fill" style={{ width: `${pct}%` }} />
      </div>
      {!role.isSystem && (
        <div className="rm-role-card-actions" onClick={e => e.stopPropagation()}>
          <Button variant="secondary" onClick={onEdit} style={{ flex: 1, fontSize: 12 }}>
            ✏️ Edit
          </Button>
          <Button variant="danger" onClick={onDelete} style={{ flex: 1, fontSize: 12 }}>
            🗑 Delete
          </Button>
        </div>
      )}
    </div>
  );
};

// ─── Employee Form ────────────────────────────────────────────────────────────

const EmployeeForm = ({ empData, setEmpData, empErrors, isEdit }) => {
  const [showPw, setShowPw] = useState(false);
  const set = (field, val) => setEmpData(prev => ({ ...prev, [field]: val }));

  return (
    <div className="rm-emp-form">
      {/* Row 1 — Name + Email */}
      <div className="rm-emp-grid">
        <div className="rm-emp-field">
          <label className="rm-emp-label">Full Name <span className="rm-req">*</span></label>
          <input
            className={`rm-emp-input${empErrors.name ? ' error' : ''}`}
            value={empData.name}
            placeholder="e.g. Sarah Johnson"
            onChange={e => set('name', e.target.value)}
          />
          {empErrors.name && <div className="rm-emp-error">{empErrors.name}</div>}
        </div>
        <div className="rm-emp-field">
          <label className="rm-emp-label">Email Address <span className="rm-req">*</span></label>
          <input
            type="email"
            className={`rm-emp-input${empErrors.email ? ' error' : ''}`}
            value={empData.email}
            placeholder="sarah@company.com"
            onChange={e => set('email', e.target.value)}
            autoComplete="off"
          />
          {empErrors.email && <div className="rm-emp-error">{empErrors.email}</div>}
        </div>
      </div>

      {/* Row 2 — Phone + Role */}
      <div className="rm-emp-grid">
        <div className="rm-emp-field">
          <label className="rm-emp-label">Phone Number <span className="rm-req">*</span></label>
          <input
            type="tel"
            className={`rm-emp-input${empErrors.phone ? ' error' : ''}`}
            value={empData.phone}
            placeholder="e.g. 9988776655"
            onChange={e => set('phone', e.target.value)}
          />
          {empErrors.phone && <div className="rm-emp-error">{empErrors.phone}</div>}
        </div>
        <div className="rm-emp-field">
          <label className="rm-emp-label">Role <span className="rm-req">*</span></label>
          <select
            className="rm-emp-select"
            value={empData.role}
            onChange={e => set('role', e.target.value)}
          >
            {USER_ROLE_OPTIONS.map(r => (
              <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Row 3 — Password */}
      <div className="rm-emp-field">
        <label className="rm-emp-label">
          Password {!isEdit && <span className="rm-req">*</span>}
          {isEdit && (
            <span className="rm-emp-label-note">leave blank to keep current</span>
          )}
        </label>
        <div className="rm-pw-wrap">
          <input
            type={showPw ? 'text' : 'password'}
            className={`rm-emp-input${empErrors.password ? ' error' : ''}`}
            value={empData.password}
            placeholder={isEdit ? '(unchanged)' : 'Min. 8 characters'}
            onChange={e => set('password', e.target.value)}
            autoComplete="new-password"
          />
          <button type="button" className="rm-pw-eye" onClick={() => setShowPw(v => !v)} tabIndex={-1}>
            {showPw ? '🙈' : '👁️'}
          </button>
        </div>
        {empErrors.password && <div className="rm-emp-error">{empErrors.password}</div>}
        {!isEdit && !empErrors.password && (
          <div className="rm-pw-hint">Must be at least 8 characters.</div>
        )}
      </div>

      {/* Active toggle */}
      <div className="rm-emp-toggle-row">
        <button
          type="button"
          className={`rm-emp-toggle${empData.isActive ? ' on' : ' off'}`}
          onClick={() => set('isActive', !empData.isActive)}
        >
          <div className="rm-emp-toggle-knob" />
        </button>
        <span className="rm-emp-toggle-label">
          {empData.isActive
            ? '✅ Account Active — employee can log in'
            : '🚫 Account Inactive — login blocked'}
        </span>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const RoleManagement = () => {
  const { showNotification } = useContext(NotificationContext);

  const [activeTab, setActiveTab] = useState('roles');

  // ── Roles state ──────────────────────────────────────────────────────────────
  const [roles, setRoles]               = useState([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);

  // ── Users state ──────────────────────────────────────────────────────────────
  const [users, setUsers]               = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userSearch, setUserSearch]     = useState('');
  const [filterRole, setFilterRole]     = useState('');

  // ── Role create/edit modal ────────────────────────────────────────────────────
  const [showFormModal, setShowFormModal] = useState(false);
  const [formMode, setFormMode]           = useState('create');
  const [editingRoleId, setEditingRoleId] = useState(null);
  const [formData, setFormData]           = useState(buildEmptyRole());
  const [formErrors, setFormErrors]       = useState({});
  const [formLoading, setFormLoading]     = useState(false);

  // ── Role delete modal ─────────────────────────────────────────────────────────
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget]       = useState(null);
  const [deleteLoading, setDeleteLoading]     = useState(false);

  // ── Employee create/edit modal ────────────────────────────────────────────────
  const [showEmpModal, setShowEmpModal]   = useState(false);
  const [empMode, setEmpMode]             = useState('create');
  const [editingEmpId, setEditingEmpId]   = useState(null);
  const [empData, setEmpData]             = useState(buildEmptyEmployee());
  const [empErrors, setEmpErrors]         = useState({});
  const [empLoading, setEmpLoading]       = useState(false);

  // ── Employee deactivate confirm ───────────────────────────────────────────────
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [deactivateTarget, setDeactivateTarget]       = useState(null);
  const [deactivateLoading, setDeactivateLoading]     = useState(false);

  // ── Inline quick role-only editing ───────────────────────────────────────────
  const [editingUserId, setEditingUserId]     = useState(null);
  const [editingUserRole, setEditingUserRole] = useState('');
  const [userRoleLoading, setUserRoleLoading] = useState(false);

  // ── Data loaders ─────────────────────────────────────────────────────────────

  const loadRoles = useCallback(async () => {
    setRolesLoading(true);
    try {
      const res = await rolesAPI.getAll();
      const list = res.data || [];
      setRoles(list);
      if (list.length > 0 && !selectedRole) setSelectedRole(list[0]);
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Failed to load roles', 'error');
    } finally {
      setRolesLoading(false);
    }
  }, [selectedRole]);

  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    try {
      const res = await rolesAPI.getAllUsers();
      setUsers(res.data || []);
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Failed to load employees', 'error');
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => { loadRoles(); }, []);
  useEffect(() => { if (activeTab === 'users') loadUsers(); }, [activeTab]);

  // ── Role handlers ─────────────────────────────────────────────────────────────

  const openCreate = () => {
    setFormData(buildEmptyRole());
    setFormErrors({});
    setFormMode('create');
    setEditingRoleId(null);
    setShowFormModal(true);
  };

  const openEdit = (role) => {
    setFormData({
      name:        role.name,
      description: role.description || '',
      permissions: JSON.parse(JSON.stringify(role.permissions)),
    });
    setFormErrors({});
    setFormMode('edit');
    setEditingRoleId(role._id);
    setShowFormModal(true);
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = 'Role name is required';
    } else if (
      roles.some(r => r.name.toLowerCase() === formData.name.trim().toLowerCase() && r._id !== editingRoleId)
    ) {
      errs.name = 'A role with this name already exists';
    }
    return errs;
  };

  const handleSave = async () => {
    const errs = validateForm();
    if (Object.keys(errs).length) { setFormErrors(errs); return; }
    setFormLoading(true);
    try {
      const payload = {
        name:        formData.name.trim(),
        description: formData.description.trim(),
        permissions: formData.permissions,
      };
      if (formMode === 'create') {
        const res = await rolesAPI.create(payload);
        const created = res.data;
        setRoles(prev => [...prev, created]);
        setSelectedRole(created);
        showNotification(`Role "${created.name}" created successfully`, 'success');
      } else {
        const res = await rolesAPI.update(editingRoleId, payload);
        const updated = res.data;
        setRoles(prev => prev.map(r => r._id === updated._id ? updated : r));
        if (selectedRole?._id === updated._id) setSelectedRole(updated);
        showNotification(`Role "${updated.name}" updated successfully`, 'success');
      }
      setShowFormModal(false);
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Failed to save role', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const handleInlinePermChange = async (newPerms) => {
    if (!selectedRole || selectedRole.isSystem) return;
    const prev = selectedRole;
    const optimistic = { ...selectedRole, permissions: newPerms };
    setSelectedRole(optimistic);
    setRoles(r => r.map(x => x._id === optimistic._id ? optimistic : x));
    try {
      const res = await rolesAPI.update(selectedRole._id, { permissions: newPerms });
      const updated = res.data;
      setSelectedRole(updated);
      setRoles(r => r.map(x => x._id === updated._id ? updated : x));
    } catch {
      setSelectedRole(prev);
      setRoles(r => r.map(x => x._id === prev._id ? prev : x));
      showNotification('Failed to save permission change', 'error');
    }
  };

  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      await rolesAPI.delete(deleteTarget._id);
      const remaining = roles.filter(r => r._id !== deleteTarget._id);
      setRoles(remaining);
      if (selectedRole?._id === deleteTarget._id) setSelectedRole(remaining[0] || null);
      showNotification(`Role "${deleteTarget.name}" deleted`, 'success');
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Failed to delete role', 'error');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Employee handlers ─────────────────────────────────────────────────────────

  const openCreateEmployee = () => {
    setEmpData(buildEmptyEmployee());
    setEmpErrors({});
    setEmpMode('create');
    setEditingEmpId(null);
    setShowEmpModal(true);
  };

  const openEditEmployee = (user) => {
    setEmpData({
      name:     user.name     || '',
      email:    user.email    || '',
      phone:    user.phone    || '',
      password: '',
      role:     user.role     || 'staff',
      isActive: user.isActive ?? true,
    });
    setEmpErrors({});
    setEmpMode('edit');
    setEditingEmpId(user._id);
    setShowEmpModal(true);
  };

  const validateEmployee = () => {
    const errs = {};
    if (!empData.name.trim())
      errs.name = 'Full name is required';
    if (!empData.email.trim())
      errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(empData.email))
      errs.email = 'Enter a valid email address';
    if (!empData.phone.trim())
      errs.phone = 'Phone number is required';
    if (empMode === 'create') {
      if (!empData.password)
        errs.password = 'Password is required';
      else if (empData.password.length < 8)
        errs.password = 'Password must be at least 8 characters';
    } else if (empData.password && empData.password.length < 8) {
      errs.password = 'Password must be at least 8 characters';
    }
    return errs;
  };

  const handleSaveEmployee = async () => {
    const errs = validateEmployee();
    if (Object.keys(errs).length) { setEmpErrors(errs); return; }
    setEmpLoading(true);
    try {
      const payload = {
        name:     empData.name.trim(),
        email:    empData.email.trim().toLowerCase(),
        phone:    empData.phone.trim(),
        role:     empData.role,
        isActive: empData.isActive,
      };
      if (empData.password) payload.password = empData.password;

      if (empMode === 'create') {
        const res = await rolesAPI.createEmployee(payload);
        setUsers(prev => [res.data, ...prev]);
        showNotification(`Employee "${res.data.name}" created successfully`, 'success');
      } else {
        const res = await rolesAPI.updateEmployee(editingEmpId, payload);
        setUsers(prev => prev.map(u => u._id === res.data._id ? res.data : u));
        showNotification(`Employee "${res.data.name}" updated successfully`, 'success');
      }
      setShowEmpModal(false);
    } catch (err) {
      console.log(err);
      
      showNotification(err?.response?.data?.message || 'Failed to save employee', 'error');
    } finally {
      setEmpLoading(false);
    }
  };

  const handleDeactivate = async () => {
    setDeactivateLoading(true);
    try {
      const res = await rolesAPI.updateEmployee(deactivateTarget._id, { isActive: false });
      setUsers(prev => prev.map(u => u._id === res.data._id ? res.data : u));
      showNotification(`${deactivateTarget.name}'s account deactivated`, 'success');
      setShowDeactivateModal(false);
      setDeactivateTarget(null);
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Failed to deactivate account', 'error');
    } finally {
      setDeactivateLoading(false);
    }
  };

  // ── Inline quick role-only save ───────────────────────────────────────────────

  const handleUserRoleSave = async (userId) => {
    setUserRoleLoading(true);
    try {
      const res = await rolesAPI.updateUserRole(userId, editingUserRole);
      const updated = res.data;
      setUsers(prev => prev.map(u => u._id === updated._id ? updated : u));
      showNotification('Role updated successfully', 'success');
      setEditingUserId(null);
    } catch (err) {
      showNotification(err?.response?.data?.message || 'Failed to update role', 'error');
    } finally {
      setUserRoleLoading(false);
    }
  };

  // ── Filtered users ────────────────────────────────────────────────────────────

  const filteredUsers = users.filter(u => {
    const q = userSearch.toLowerCase();
    const matchSearch = !userSearch ||
      u.name?.toLowerCase().includes(q) ||
      u.email?.toLowerCase().includes(q) ||
      u.phone?.toLowerCase().includes(q) ||
      u.role?.toLowerCase().includes(q);
    const matchRole = !filterRole || u.role === filterRole;
    return matchSearch && matchRole;
  });

  const hasUserFilters = !!filterRole;

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="ps-root">

      {/* ── Header — button switches context based on active tab ─────────────────── */}
      <div className="ps-header">
        <div>
          <h1 className="ps-title">Roles & Permissions</h1>
          <p className="ps-subtitle">Manage access levels and employee accounts</p>
        </div>
        {activeTab === 'roles'
          ? <Button variant="primary" onClick={openCreate}>+ Create Role</Button>
          : <Button variant="primary" onClick={openCreateEmployee}>+ Add Employee</Button>
        }
      </div>

      {/* ── Summary Cards ────────────────────────────────────────────────────────── */}
      <div className="ps-summary-row">
        <SummaryCard icon="🔐" label="Total Roles"      accent="#3b82f6"
          value={roles.length}
          sub={`${roles.filter(r => r.isSystem).length} system roles`} />
        <SummaryCard icon="👥" label="Total Employees"  accent="#10b981"
          value={users.length}
          sub="registered accounts" />
        <SummaryCard icon="✅" label="Active Employees" accent="#6366f1"
          value={users.filter(u => u.isActive).length}
          sub="currently active" />
        <SummaryCard icon="🛡️" label="Custom Roles"    accent="#f59e0b"
          value={roles.filter(r => !r.isSystem).length}
          sub="user-defined roles" />
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────────────── */}
      <div className="ps-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`ps-tab${activeTab === tab.id ? ' active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            <span className="ps-tab-icon">{tab.icon}</span>
            {tab.label}
            <span className="rm-tab-count">
              {tab.id === 'roles' ? roles.length : users.length}
            </span>
          </button>
        ))}
      </div>

      {/* ── Content ──────────────────────────────────────────────────────────────── */}
      <div className="ps-content">
        <div className="ps-tab-pane">

          {/* ══════════ ROLES TAB ══════════ */}
          {activeTab === 'roles' && (
            <>
              <div className="ps-table-meta">
                <span className="ps-count">{roles.length} role{roles.length !== 1 ? 's' : ''}</span>
                <Button variant="secondary" onClick={loadRoles}>↺ Refresh</Button>
              </div>

              {rolesLoading ? (
                <div className="ps-loading">Loading roles…</div>
              ) : (
                <div className="rm-layout">

                  {/* Left: role cards */}
                  <div>
                    <div className="rm-role-list-label">All Roles ({roles.length})</div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {roles.length === 0 ? (
                        <div className="ps-empty" style={{ padding: '32px 0' }}>
                          <div style={{ fontSize: 32, marginBottom: 8 }}>🔐</div>
                          No roles yet. Create one to get started.
                        </div>
                      ) : roles.map(role => (
                        <RoleCard
                          key={role._id}
                          role={role}
                          isSelected={selectedRole?._id === role._id}
                          onClick={() => setSelectedRole(role)}
                          onEdit={() => openEdit(role)}
                          onDelete={() => { setDeleteTarget(role); setShowDeleteModal(true); }}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Right: permission detail panel */}
                  {selectedRole ? (
                    <div className="rm-detail-panel">
                      <div className="rm-detail-header">
                        <div>
                          <h2 className="rm-detail-title">
                            {selectedRole.name}
                            {selectedRole.isSystem && (
                              <span className="rm-badge" style={{ background: '#fef3c7', color: '#d97706', fontSize: 11 }}>
                                System
                              </span>
                            )}
                          </h2>
                          <p className="rm-detail-desc">
                            {selectedRole.description || 'No description provided'}
                          </p>
                        </div>
                        {!selectedRole.isSystem && (
                          <Button variant="secondary" onClick={() => openEdit(selectedRole)}>
                            ✏️ Edit Role
                          </Button>
                        )}
                      </div>

                      <div className="rm-detail-legend">
                        <div className="rm-legend-item">
                          <div className="rm-toggle on" style={{ cursor: 'default', pointerEvents: 'none' }}>✓</div>
                          <span>Granted</span>
                        </div>
                        <div className="rm-legend-item">
                          <div className="rm-toggle off" style={{ cursor: 'default', pointerEvents: 'none' }} />
                          <span>Not granted</span>
                        </div>
                        <div className="rm-legend-item">
                          <div className="rm-perm-dash"><div className="rm-perm-dash-line" /></div>
                          <span>Not applicable</span>
                        </div>
                        {selectedRole.isSystem && (
                          <span style={{ marginLeft: 'auto', fontSize: 12, color: '#d97706' }}>
                            🔒 System role — read only
                          </span>
                        )}
                      </div>

                      {selectedRole.isSystem && (
                        <div style={{ padding: '10px 22px' }}>
                          <div className="rm-system-banner">
                            ⚠️ System roles cannot be modified. Create a new role to customise permissions.
                          </div>
                        </div>
                      )}

                      <div className="rm-detail-body">
                        <PermissionGrid
                          permissions={selectedRole.permissions}
                          onChange={handleInlinePermChange}
                          readOnly={selectedRole.isSystem}
                        />
                      </div>

                      {!selectedRole.isSystem && (
                        <div className="rm-detail-footer">
                          💡 Toggling permissions saves to the server automatically.
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="rm-detail-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
                      <div className="ps-empty">
                        <div style={{ fontSize: 36, marginBottom: 8 }}>👈</div>
                        Select a role to view its permissions
                      </div>
                    </div>
                  )}

                </div>
              )}
            </>
          )}

          {/* ══════════ USERS TAB ══════════ */}
          {activeTab === 'users' && (
            <>
              {/* Filters bar */}
              <div className="ps-filters">
                <div className="ps-search-wrap">
                  <span className="ps-search-icon">🔍</span>
                  <input
                    className="ps-search-input"
                    placeholder="Search by name, email, phone or role…"
                    value={userSearch}
                    onChange={e => setUserSearch(e.target.value)}
                  />
                  {userSearch && (
                    <button className="rm-search-clear" onClick={() => setUserSearch('')}>✕</button>
                  )}
                </div>

                <select
                  className="ps-filter-select"
                  value={filterRole}
                  onChange={e => setFilterRole(e.target.value)}
                >
                  <option value="">All Roles</option>
                  {USER_ROLE_OPTIONS.map(r => (
                    <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
                  ))}
                </select>

                {hasUserFilters && (
                  <button className="rm-clear-filters" onClick={() => setFilterRole('')}>
                    Clear ✕
                  </button>
                )}

                <Button variant="secondary" onClick={loadUsers}>↺</Button>
              </div>

              {/* Meta row */}
              <div className="ps-table-meta">
                <span className="ps-count">
                  {filteredUsers.length} employee{filteredUsers.length !== 1 ? 's' : ''}
                </span>
                <span style={{ fontSize: 13, color: '#64748b' }}>
                  {users.filter(u => u.isActive).length} active · {users.filter(u => !u.isActive).length} inactive
                </span>
              </div>

              {usersLoading ? (
                <div className="ps-loading">Loading employees…</div>
              ) : (
                <div className="ps-table-wrap">
                  <table className="ps-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Role</th>
                        <th>Status</th>
                        <th>Last Login</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="ps-empty">
                            <div style={{ fontSize: 36, marginBottom: 8 }}>👥</div>
                            No employees found.{' '}
                            <button
                              onClick={openCreateEmployee}
                              style={{ background: 'none', border: 'none', color: '#3b82f6', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
                            >
                              Add the first employee →
                            </button>
                          </td>
                        </tr>
                      ) : filteredUsers.map(user => (
                        <tr key={user._id} className="ps-row" style={{ cursor: 'default' }}>

                          {/* Name + avatar */}
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div className="rm-user-avatar">
                                {user.name?.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
                                  {user.name}
                                </div>
                                <div style={{ fontSize: 11, color: '#94a3b8' }}>
                                  Joined {user.createdAt
                                    ? new Date(user.createdAt).toLocaleDateString('en-KW')
                                    : '—'}
                                </div>
                              </div>
                            </div>
                          </td>

                          <td style={{ fontSize: 13, color: '#64748b' }}>{user.email || '—'}</td>
                          <td style={{ fontSize: 13, color: '#64748b' }}>{user.phone || '—'}</td>

                          {/* Role — inline quick-edit */}
                          <td>
                            {editingUserId === user._id ? (
                              <select
                                className="rm-role-select"
                                value={editingUserRole}
                                onChange={e => setEditingUserRole(e.target.value)}
                                autoFocus
                              >
                                {USER_ROLE_OPTIONS.map(r => (
                                  <option key={r} value={r}>
                                    {r.charAt(0).toUpperCase() + r.slice(1)}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <RoleBadge name={user.role} />
                            )}
                          </td>

                          <td><StatusBadge active={user.isActive} /></td>

                          <td className="ps-date">
                            {user.lastLogin
                              ? new Date(user.lastLogin).toLocaleDateString('en-KW')
                              : 'Never'}
                          </td>

                          {/* Actions */}
                          <td onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', gap: 4 }}>
                              {editingUserId === user._id ? (
                                <>
                                  <Button
                                    variant="primary"
                                    onClick={() => handleUserRoleSave(user._id)}
                                    disabled={userRoleLoading}
                                    style={{ fontSize: 12 }}
                                  >
                                    {userRoleLoading ? '…' : 'Save'}
                                  </Button>
                                  <Button
                                    variant="secondary"
                                    onClick={() => setEditingUserId(null)}
                                    style={{ fontSize: 12 }}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : (
                                <>
                                  {/* Full edit */}
                                  <button
                                    className="rm-action-btn"
                                    onClick={() => openEditEmployee(user)}
                                    title="Edit employee details"
                                  >
                                    ✏️
                                  </button>
                                  {/* Quick role-only change */}
                                  <button
                                    className="rm-action-btn"
                                    onClick={() => { setEditingUserId(user._id); setEditingUserRole(user.role); }}
                                    title="Quick role change"
                                  >
                                    🔐
                                  </button>
                                  {/* Deactivate (only if active) */}
                                  {user.isActive && (
                                    <button
                                      className="rm-action-btn danger"
                                      onClick={() => { setDeactivateTarget(user); setShowDeactivateModal(true); }}
                                      title="Deactivate account"
                                    >
                                      🚫
                                    </button>
                                  )}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}

        </div>
      </div>

      {/* ════════════════════════════════════════════════════════
          ROLE CREATE / EDIT MODAL
          ════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={formMode === 'create' ? '+ Create New Role' : '✏️ Edit Role'}
        size="large"
      >
        <div className="ps-modal-form">
          <div className="rm-form-grid">
            <div className="rm-form-field">
              <label>Role Name *</label>
              <input
                className={`rm-form-input${formErrors.name ? ' error' : ''}`}
                value={formData.name}
                placeholder="e.g. Cashier, Supervisor"
                onChange={e => {
                  setFormData(prev => ({ ...prev, name: e.target.value }));
                  if (formErrors.name) setFormErrors(prev => ({ ...prev, name: '' }));
                }}
              />
              {formErrors.name && (
                <div className="rm-field-error">{formErrors.name}</div>
              )}
            </div>
            <div className="rm-form-field">
              <label>Description</label>
              <input
                className="rm-form-input"
                value={formData.description}
                placeholder="Brief description of this role"
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
              />
            </div>
          </div>

          <div className="ps-modal-field">
            <div className="rm-perm-section-label">Permissions</div>
            <div className="rm-perm-border-box">
              <PermissionGrid
                permissions={formData.permissions}
                onChange={perms => setFormData(prev => ({ ...prev, permissions: perms }))}
                readOnly={false}
              />
            </div>
          </div>

          <div className="ps-modal-actions">
            <Button variant="secondary" onClick={() => setShowFormModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSave} disabled={formLoading}>
              {formLoading ? 'Saving…' : formMode === 'create' ? 'Create Role' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          EMPLOYEE CREATE / EDIT MODAL
          ════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showEmpModal}
        onClose={() => setShowEmpModal(false)}
        title={empMode === 'create' ? '👤 Add New Employee' : `✏️ Edit: ${empData.name || 'Employee'}`}
        size="medium"
      >
        <div className="ps-modal-form">
          <EmployeeForm
            empData={empData}
            setEmpData={setEmpData}
            empErrors={empErrors}
            isEdit={empMode === 'edit'}
          />
          <div className="ps-modal-actions">
            <Button variant="secondary" onClick={() => setShowEmpModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleSaveEmployee} disabled={empLoading}>
              {empLoading
                ? 'Saving…'
                : empMode === 'create' ? 'Create Employee Account' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ════════════════════════════════════════════════════════
          ROLE DELETE CONFIRM
          ════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteTarget(null); }}
        title="Delete Role"
      >
        {deleteTarget && (
          <div className="ps-modal-form">
            <div className="rm-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  Delete role "{deleteTarget.name}"?
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  This action cannot be undone. Employees assigned this role will lose their configured access.
                </div>
              </div>
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowDeleteModal(false); setDeleteTarget(null); }}>Cancel</Button>
              <Button variant="danger" onClick={handleDelete} disabled={deleteLoading}>
                {deleteLoading ? 'Deleting…' : 'Delete Role'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* ════════════════════════════════════════════════════════
          EMPLOYEE DEACTIVATE CONFIRM
          ════════════════════════════════════════════════════════ */}
      <Modal
        isOpen={showDeactivateModal}
        onClose={() => { setShowDeactivateModal(false); setDeactivateTarget(null); }}
        title="Deactivate Account"
      >
        {deactivateTarget && (
          <div className="ps-modal-form">
            <div className="rm-delete-warning">
              <span style={{ fontSize: 32 }}>⚠️</span>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#0f172a', marginBottom: 4 }}>
                  Deactivate {deactivateTarget.name}'s account?
                </div>
                <div style={{ fontSize: 13, color: '#64748b' }}>
                  They will no longer be able to log in. You can reactivate the account at any time by editing the employee profile.
                </div>
              </div>
            </div>
            <div className="ps-modal-actions">
              <Button variant="secondary" onClick={() => { setShowDeactivateModal(false); setDeactivateTarget(null); }}>Cancel</Button>
              <Button variant="danger" onClick={handleDeactivate} disabled={deactivateLoading}>
                {deactivateLoading ? 'Deactivating…' : 'Deactivate Account'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

    </div>
  );
};

export default RoleManagement;
